import React, { useRef, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  FlatList,
  Dimensions,
  Image,
  Animated,
  Easing,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';
import { Colors, SIZES } from '../constants/theme';
import { VERTICAL_ESCOLAR_VISIBLE } from '../constants/featureFlags';

/**
 * ONBOARDING DE PRIMERA VEZ
 * ----------------------------------------------------------------------------
 * 3 slides swipeables que se muestran SOLO la primera vez que el usuario
 * abre la app. Después de pasarlos (o tocar Saltar), se guarda flag en
 * AsyncStorage para no volver a mostrarlos jamás.
 *
 * Slides:
 *   1. Tu seguridad primero        — KYC revisado a mano + escudo verde
 *   2. 0% Comisión a tu trabajo    — choferes 100%, restaurantes 100%
 *   3. Una sola app, todo a la mano — viajes + comida + paquetes + rent-car
 *                                     (escolar solo si VERTICAL_ESCOLAR_VISIBLE)
 */

const ONBOARDING_DONE_KEY = 'gs_onboarding_done';

export async function isOnboardingDone(): Promise<boolean> {
  try {
    const v = await AsyncStorage.getItem(ONBOARDING_DONE_KEY);
    return v === '1';
  } catch {
    return false;
  }
}

export async function markOnboardingDone(): Promise<void> {
  try {
    await AsyncStorage.setItem(ONBOARDING_DONE_KEY, '1');
  } catch {
    // silent
  }
}

const { width: SCREEN_WIDTH } = Dimensions.get('window');

interface Slide {
  id: string;
  title: string;
  subtitle: string;
  icon: keyof typeof Ionicons.glyphMap;
  bgGradient: [string, string];
  iconColor: string;
}

// ═══════════════════════════════════════════════════════════════════════════
// ESTA ES LA PRIMERA PANTALLA DE LA APP — Y LA PRIMERA QUE LEE EL REVISOR.
// Cada frase de aquí se mide contra src/app/terms.tsx y src/app/privacy.tsx.
// Si una promesa de este archivo no se puede señalar con el dedo en el código,
// se cambia AQUÍ, no en los documentos legales.
// ═══════════════════════════════════════════════════════════════════════════
const SLIDES: Slide[] = [
  {
    id: '1',
    // ── Decía "Verificación biométrica de identidad" y "Cero anónimos". ──
    // Las dos cosas las niega la propia app:
    //   · terms.tsx punto 4 y privacy.tsx punto 2.c dicen, en negrita, que la
    //     selfie "la compara a mano una persona autorizada de Go Safely" y que
    //     "no usamos reconocimiento facial, prueba de vida ni ningún sistema
    //     automático". Anunciar biometría en la PRIMERA pantalla es justo la
    //     declaración de datos biométricos por la que ya nos rechazaron.
    //   · "Cero anónimos" es falso mientras KYC_WALL_ENFORCED = false
    //     (featureFlags.ts): el pasajero entra al mapa y pide su viaje sin
    //     verificarse — se lo dice kyc-welcome.tsx en "Ahora no".
    title: 'Tu seguridad, primero',
    subtitle:
      'Verificamos la identidad de quien conduce: una persona de nuestro equipo compara su selfie con su cédula. Choferes con KYC aprobado y pasajeros con escudo verde.',
    icon: 'shield-checkmark',
    bgGradient: ['#0066FF', '#003D99'],
    iconColor: '#FFF',
  },
  {
    id: '2',
    // ── Decía "una suscripción mensual baja — sin sorpresas". ──
    // Dos choques con terms.tsx:
    //   · el punto 14 avisa que los Términos (y con ellos los precios) pueden
    //     cambiar avisando con 15 días — "sin sorpresas" promete lo contrario;
    //   · el punto 3 lista CUATRO cobros posibles, no uno: suscripción, plan
    //     publicitario del restaurante, multa por cancelaciones y cupo del 4º
    //     vehículo del Rent Car. Decir "solo pagas una suscripción" esconde
    //     tres. Se deja el hecho comprobable: los 30 días de prueba gratis del
    //     punto 6, que también anuncia kyc-welcome.tsx en cada tarjeta.
    title: '0% de comisión a tu trabajo',
    subtitle:
      'Los choferes se llevan el 100% del viaje. Los restaurantes el 100% del pedido. Pagas una cuota mensual para trabajar, con 30 días de prueba gratis.',
    icon: 'cash',
    bgGradient: ['#10B981', '#047857'],
    iconColor: '#FFF',
  },
  {
    id: '3',
    // ── Anunciaba "supervisa la ruta escolar de tus hijos". ──
    // privacy.tsx punto 10 dice literalmente "Transporte escolar (no
    // disponible en esta versión)", y VERTICAL_ESCOLAR_VISIBLE está en false
    // (featureFlags.ts): las tarjetas del director y del bus escolar están
    // ocultas en role-selection.tsx y kyc-welcome.tsx. El texto sigue a la
    // bandera —igual que en role-selection.tsx— para que vuelva solo el día
    // que el vertical se encienda. Lo que queda listado es lo que sí tiene
    // pantalla hoy: viajes, comida, envío de paquete (map.tsx) y rent car.
    title: 'Una sola app, todo a la mano',
    subtitle: VERTICAL_ESCOLAR_VISIBLE
      ? 'Pide un taxi, ordena comida, envía un paquete, alquila un vehículo y supervisa la ruta escolar de tus hijos. Todo desde el mismo lugar, en tiempo real.'
      : 'Pide un taxi o un motoconcho, ordena comida, envía un paquete y alquila un vehículo para trabajar. Todo desde el mismo lugar, en tiempo real.',
    icon: 'apps',
    bgGradient: ['#FF6B35', '#D63A1B'],
    iconColor: '#FFF',
  },
];

export default function OnboardingScreen() {
  const router = useRouter();
  const listRef = useRef<FlatList<Slide>>(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const finish = async () => {
    await markOnboardingDone();
    router.replace('/');
  };

  const next = () => {
    if (activeIndex < SLIDES.length - 1) {
      listRef.current?.scrollToIndex({ index: activeIndex + 1, animated: true });
    } else {
      finish();
    }
  };

  const onScroll = (e: any) => {
    const x = e.nativeEvent.contentOffset.x;
    const idx = Math.round(x / SCREEN_WIDTH);
    if (idx !== activeIndex) setActiveIndex(idx);
  };

  return (
    <View style={styles.root}>
      <FlatList
        ref={listRef}
        data={SLIDES}
        keyExtractor={(s) => s.id}
        horizontal
        pagingEnabled
        showsHorizontalScrollIndicator={false}
        onMomentumScrollEnd={onScroll}
        renderItem={({ item, index }) => <SlideView slide={item} isActive={activeIndex === index} />}
      />

      <SafeAreaView pointerEvents="box-none" style={StyleSheet.absoluteFill}>
        {/* Skip arriba a la derecha */}
        <View style={styles.topRow}>
          <TouchableOpacity onPress={finish} style={styles.skipBtn}>
            <Text style={styles.skipText}>Saltar</Text>
          </TouchableOpacity>
        </View>

        {/* Dots + Next abajo */}
        <View style={styles.bottomRow}>
          <View style={styles.dots}>
            {SLIDES.map((_, i) => (
              <View
                key={i}
                style={[
                  styles.dot,
                  activeIndex === i && styles.dotActive,
                ]}
              />
            ))}
          </View>
          <TouchableOpacity style={styles.nextBtn} onPress={next}>
            <Text style={styles.nextBtnText}>
              {activeIndex < SLIDES.length - 1 ? 'Siguiente' : 'Empezar'}
            </Text>
            <Ionicons name="arrow-forward" size={18} color="#FFF" style={{ marginLeft: 6 }} />
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    </View>
  );
}

function SlideView({ slide, isActive }: { slide: Slide; isActive: boolean }) {
  const scale = useRef(new Animated.Value(0.8)).current;
  const fade = useRef(new Animated.Value(0)).current;

  React.useEffect(() => {
    if (isActive) {
      Animated.parallel([
        Animated.spring(scale, { toValue: 1, friction: 6, tension: 60, useNativeDriver: true }),
        Animated.timing(fade, { toValue: 1, duration: 500, easing: Easing.out(Easing.cubic), useNativeDriver: true }),
      ]).start();
    } else {
      scale.setValue(0.8);
      fade.setValue(0);
    }
  }, [isActive, scale, fade]);

  return (
    <LinearGradient
      colors={slide.bgGradient}
      style={{ width: SCREEN_WIDTH, height: '100%' }}
    >
      <View style={styles.slideContent}>
        <Animated.View
          style={{
            opacity: fade,
            transform: [{ scale }],
            alignItems: 'center',
          }}
        >
          <View style={styles.iconBg}>
            <Ionicons name={slide.icon} size={80} color={slide.iconColor} />
          </View>
          <Text style={styles.slideTitle}>{slide.title}</Text>
          <Text style={styles.slideSubtitle}>{slide.subtitle}</Text>
        </Animated.View>
      </View>
    </LinearGradient>
  );
}

const styles = StyleSheet.create({
  root: { flex: 1, backgroundColor: '#000' },
  topRow: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    padding: SIZES.padding,
  },
  skipBtn: {
    paddingHorizontal: 14,
    paddingVertical: 8,
    borderRadius: 16,
    backgroundColor: 'rgba(255,255,255,0.18)',
  },
  skipText: { color: '#FFF', fontWeight: '700', fontSize: 13 },
  slideContent: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 32,
  },
  iconBg: {
    width: 160,
    height: 160,
    borderRadius: 80,
    backgroundColor: 'rgba(255,255,255,0.15)',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 40,
    borderWidth: 1,
    borderColor: 'rgba(255,255,255,0.25)',
  },
  slideTitle: {
    fontSize: 28,
    fontWeight: '900',
    color: '#FFF',
    textAlign: 'center',
    marginBottom: 16,
    letterSpacing: -0.5,
  },
  slideSubtitle: {
    fontSize: 16,
    color: 'rgba(255,255,255,0.92)',
    textAlign: 'center',
    lineHeight: 24,
    paddingHorizontal: 12,
  },
  bottomRow: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: SIZES.padding,
    paddingBottom: 30,
  },
  dots: { flexDirection: 'row' },
  dot: {
    width: 7,
    height: 7,
    borderRadius: 4,
    backgroundColor: 'rgba(255,255,255,0.4)',
    marginRight: 6,
  },
  dotActive: {
    width: 24,
    backgroundColor: '#FFF',
  },
  nextBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(0,0,0,0.4)',
    borderColor: 'rgba(255,255,255,0.4)',
    borderWidth: 1,
    paddingHorizontal: 18,
    paddingVertical: 10,
    borderRadius: 24,
  },
  nextBtnText: { color: '#FFF', fontWeight: '700', fontSize: 14 },
});
